// Mutation hooks for the Manage Access panel (KANP-1475).
//
// Every mutation invalidates `dataContractsKeys.access(contractId)` on
// success so the panel re-renders with fresh tuple state. This is the
// staleness-mitigation pattern Joshua Pheneger flagged in the #authz
// thread on 2026-06-10: server returns raw FGA tuples (no baked
// permissions block), client refetches on every write — the UI never
// shows a permission state that differs from FGA truth for more than
// the refetch round-trip.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  ChangeContractAccessRequest,
  GrantContractAccessRequest
} from '@/types/dataContracts';

import { dataContractsKeys } from '../queries/useDataContracts';

export function useGrantContractAccess( contractId: string ) {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( body: GrantContractAccessRequest ) =>
      APIGateway.dataContracts.grantContractAccess( contractId, body ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.access( contractId ) } );
    }
  } );
}

export function useChangeContractAccess( contractId: string ) {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( { principal, body }: { principal: string; body: ChangeContractAccessRequest } ) =>
      APIGateway.dataContracts.changeContractAccess( contractId, principal, body ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.access( contractId ) } );
    }
  } );
}

export function useRevokeContractAccess( contractId: string ) {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( principal: string ) =>
      APIGateway.dataContracts.revokeContractAccess( contractId, principal ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.access( contractId ) } );
    }
  } );
}

export function useSetContractRestrict( contractId: string ) {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( isPrivate: boolean ) =>
      APIGateway.dataContracts.setContractRestrict( contractId, { private: isPrivate } ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.access( contractId ) } );
    }
  } );
}
